import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listDrafts,
  getDraft,
  createDraft,
  updateDraft,
  deleteDraft,
} from '@/services/operationDraftApi';

// Universal draft-recovery hook for scan-heavy operations (bundle receipt,
// internal transfer, inhouse combined receipt+PI, ...). Auto-saves a snapshot
// of the in-progress form every AUTO_SAVE_EVERY scans and after a short idle,
// so operators can resume after a network / power failure.
//
// saveNow / autoSave are kept STABLE across renders (activeDraftId lives in a
// ref) so a screen's auto-save effect only re-runs on real form changes — not
// when the active draft id flips. This prevents a just-deleted active draft
// from being immediately recreated.
const AUTO_SAVE_EVERY = 25;
const IDLE_DEBOUNCE_MS = 3000;

export function useOperationDraft({ moduleKey, operationType, enabled = true, allowZero = false }) {
  const [drafts, setDrafts] = useState([]);
  const [activeDraftId, setActiveDraftIdState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const activeDraftIdRef = useRef(null);
  const lastSavedCountRef = useRef(0);
  const idleTimerRef = useRef(null);
  const latestSnapshotRef = useRef(null);
  const pendingSnapshotRef = useRef(null);
  const saveNowRef = useRef(null);
  const savingRef = useRef(false);

  const setActiveDraftId = useCallback((id) => {
    activeDraftIdRef.current = id;
    setActiveDraftIdState(id);
  }, []);

  const refreshDrafts = useCallback(async () => {
    if (!enabled || !moduleKey) return;
    try {
      const res = await listDrafts(moduleKey, operationType);
      setDrafts(res.drafts || []);
    } catch {
      // network hiccup — drafts list is non-critical, keep the last known value
    }
  }, [moduleKey, operationType, enabled]);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  // Persist the current snapshot now (create on first save, else update).
  // If a save is already in flight, the latest snapshot is stashed and drained
  // once the current request finishes — so no scan is ever silently dropped.
  const saveNow = useCallback(
    async (snapshot = {}) => {
      const { label, bundleCount = 0, payload = {}, documentId = null, documentNo = null } = snapshot;
      if (!enabled || !moduleKey) return null;
      if (savingRef.current) {
        pendingSnapshotRef.current = { label, bundleCount, payload, documentId, documentNo };
        return null;
      }
      savingRef.current = true;
      setSaving(true);
      try {
        const currentId = activeDraftIdRef.current;
        if (currentId) {
          const res = await updateDraft(currentId, {
            label,
            bundle_count: bundleCount,
            payload,
          });
          lastSavedCountRef.current = bundleCount;
          setLastSavedAt(res.updated_at || new Date().toISOString());
          return currentId;
        }
        const res = await createDraft({
          module_key: moduleKey,
          operation_type: operationType,
          document_id: documentId,
          document_no: documentNo,
          label,
          bundle_count: bundleCount,
          payload,
        });
        setActiveDraftId(res.draft_id);
        // Adopt the shared draft's true count (an existing richer draft is not
        // clobbered server-side), so we don't immediately re-save an emptier snapshot.
        lastSavedCountRef.current = typeof res.bundle_count === 'number' ? res.bundle_count : bundleCount;
        setLastSavedAt(res.updated_at || new Date().toISOString());
        refreshDrafts();
        return res.draft_id;
      } catch {
        return null;
      } finally {
        savingRef.current = false;
        setSaving(false);
        // Drain a snapshot that arrived while this save was in flight.
        const pending = pendingSnapshotRef.current;
        pendingSnapshotRef.current = null;
        if (pending && pending.bundleCount !== lastSavedCountRef.current) {
          setTimeout(() => saveNowRef.current?.(pending), 0);
        }
      }
    },
    [enabled, moduleKey, operationType, refreshDrafts, setActiveDraftId]
  );

  useEffect(() => {
    saveNowRef.current = saveNow;
  }, [saveNow]);

  // Call on every form change with the full snapshot. Saves immediately once
  // AUTO_SAVE_EVERY new bundles have accumulated; a trailing idle save always
  // runs as a safety net (it no-ops if nothing new since the last save).
  const autoSave = useCallback(
    ({ label, bundleCount = 0, payload = {}, documentId = null, documentNo = null } = {}) => {
      if (!enabled || !moduleKey) return;
      latestSnapshotRef.current = { label, bundleCount, payload, documentId, documentNo };
      // For scan screens, don't persist an empty form. For "bookmark" screens
      // (allowZero) the selection itself is recoverable, so persist at 0 too.
      if (bundleCount <= 0 && !allowZero) return;

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        const snap = latestSnapshotRef.current;
        if (!snap) return;
        const needsInitial = allowZero && activeDraftIdRef.current == null;
        const countChanged = snap.bundleCount !== lastSavedCountRef.current;
        if ((snap.bundleCount > 0 || allowZero) && (needsInitial || countChanged)) {
          saveNow(snap);
        }
      }, IDLE_DEBOUNCE_MS);

      if (bundleCount - lastSavedCountRef.current >= AUTO_SAVE_EVERY) {
        saveNow(latestSnapshotRef.current);
      }
    },
    [enabled, moduleKey, allowZero, saveNow]
  );

  // Delete the active draft (used after a successful submit).
  const clearDraft = useCallback(async () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    latestSnapshotRef.current = null;
    const currentId = activeDraftIdRef.current;
    if (currentId) {
      try {
        await deleteDraft(currentId);
      } catch {
        // best-effort
      }
    }
    setActiveDraftId(null);
    lastSavedCountRef.current = 0;
    setLastSavedAt(null);
    refreshDrafts();
  }, [refreshDrafts, setActiveDraftId]);

  // Fetch a draft's full payload and adopt it as the active draft.
  const loadDraft = useCallback(
    async (draftId) => {
      const doc = await getDraft(draftId);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      setActiveDraftId(draftId);
      lastSavedCountRef.current = doc.bundle_count || 0;
      setLastSavedAt(doc.updated_at || null);
      latestSnapshotRef.current = null;
      return doc;
    },
    [setActiveDraftId]
  );

  // Delete a specific draft from the list (menu delete action).
  // Returns true on success, false if the server delete failed (so callers such
  // as "Start Fresh" can keep the draft and warn instead of silently dropping it).
  const removeDraft = useCallback(
    async (draftId) => {
      try {
        await deleteDraft(draftId);
      } catch {
        return false;
      }
      if (draftId === activeDraftIdRef.current) {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        latestSnapshotRef.current = null;
        setActiveDraftId(null);
        lastSavedCountRef.current = 0;
        setLastSavedAt(null);
      }
      refreshDrafts();
      return true;
    },
    [refreshDrafts, setActiveDraftId]
  );

  useEffect(
    () => () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    },
    []
  );

  return {
    drafts,
    activeDraftId,
    saving,
    lastSavedAt,
    autoSave,
    saveNow,
    clearDraft,
    loadDraft,
    removeDraft,
    refreshDrafts,
    setActiveDraftId,
  };
}

export default useOperationDraft;
